import { useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import {
  UnifiedFileWithPreview,
  UploadContext,
  UploadQueueItem,
  DEFAULT_UPLOAD_CONFIGS,
} from "../types/unifiedUploadTypes";
import {
  uploadFileInChunks,
  validateFileForContext,
  calculateOptimalChunkSize,
  cancelUpload as cancelUploadAPI,
} from "../services/unifiedChunkedUploadService";
import { fetchUploadLimits } from "../services/uploadLimitsService";
import { formatFileSize } from "src/helpers/formatFileSize";

interface UseUnifiedChunkedUploadProps {
  context: UploadContext;
  contextId?: string | number | null;
  onFilesChange?: (files: UnifiedFileWithPreview[]) => void;
  onUploadComplete?: (file: UnifiedFileWithPreview, result: any) => void;
  maxFiles?: number;
  maxConcurrent?: number;
  autoUpload?: boolean;
  customValidation?: (file: File) => { isValid: boolean; error?: string };
}

interface UseUnifiedChunkedUploadReturn {
  files: UnifiedFileWithPreview[];
  uploadError: string | null;
  isDragActive: boolean;
  isUploading: boolean;
  uploadQueue: UploadQueueItem[];
  handleFileUpload: (acceptedFiles: File[]) => Promise<void>;
  removeFile: (index: number) => Promise<void>;
  cancelUpload: (fileId: string) => Promise<void>;
  retryUpload: (fileId: string) => Promise<void>;
  clearError: () => void;
  getRootProps: () => any;
  getInputProps: () => any;
  openFileDialog: () => void;
}

// Generate unique ID for files
const generateFileId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const useUnifiedChunkedUpload = ({
  context,
  contextId,
  onFilesChange,
  onUploadComplete,
  maxFiles = 10000, // Allow many files by default
  maxConcurrent = 10, // Default, will be overridden by server limit
  autoUpload = true,
  customValidation,
}: UseUnifiedChunkedUploadProps): UseUnifiedChunkedUploadReturn => {
  // State for server-provided limits
  const [serverMaxConcurrent, setServerMaxConcurrent] = useState<number>(10);

  // Ensure maxConcurrent doesn't exceed server limit
  const effectiveMaxConcurrent = Math.min(maxConcurrent, serverMaxConcurrent);
  const [files, setFiles] = useState<UnifiedFileWithPreview[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const [queueVersion, setQueueVersion] = useState<number>(0); // Force re-render

  // Refs for managing uploads
  const uploadControllers = useRef(new Map<string, AbortController>()).current;
  const uploadPromises = useRef(new Map<string, Promise<any>>()).current;
  const activeUploadIds = useRef(new Set<string>()).current;

  // Get context configuration
  const contextConfig =
    DEFAULT_UPLOAD_CONFIGS[context] || DEFAULT_UPLOAD_CONFIGS.dialogue;

  // Fetch server upload limits on mount
  useEffect(() => {
    fetchUploadLimits()
      .then((limits) => {
        setServerMaxConcurrent(limits.max_concurrent_uploads);
        console.log(
          `📊 Server upload limit fetched: ${limits.max_concurrent_uploads} concurrent uploads`,
        );
      })
      .catch((error) => {
        console.error("Failed to fetch upload limits, using default:", error);
      });
  }, []);

  // Notify parent component when files change
  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(files);
    }
  }, [files, onFilesChange]);

  // Track if we're currently processing to avoid race conditions
  const isProcessingRef = useRef(false);

  // Process upload queue
  useEffect(() => {
    if (!autoUpload || !contextId) return;
    if (isProcessingRef.current) return; // Prevent concurrent processing

    const processQueue = async () => {
      isProcessingRef.current = true;

      try {
        // Get truly pending items (not already being processed)
        const pendingItems = uploadQueue.filter(
          (item) => item.status === "pending" && !activeUploadIds.has(item.id),
        );

        // Get current active count from our tracking set
        const currentActiveCount = activeUploadIds.size;
        const availableSlots = Math.max(
          0,
          effectiveMaxConcurrent - currentActiveCount,
        );

        console.log(
          `🔍 Queue check - Active: ${currentActiveCount}/${effectiveMaxConcurrent}, ` +
            `Pending: ${pendingItems.length}, Available slots: ${availableSlots}`,
        );

        if (availableSlots === 0 || pendingItems.length === 0) {
          // Log when we're at capacity
          if (availableSlots === 0 && pendingItems.length > 0) {
            console.log(
              `⏳ Upload queue at capacity (${currentActiveCount}/${effectiveMaxConcurrent}). ` +
                `${pendingItems.length} files waiting in queue.`,
            );
          }
          return;
        }

        // Only process what we have slots for
        const itemsToProcess = pendingItems.slice(0, availableSlots);
        const itemIds = itemsToProcess.map((item) => item.id);

        console.log(
          `📤 Starting ${itemsToProcess.length} uploads. ` +
            `Active will be: ${
              currentActiveCount + itemsToProcess.length
            }/${effectiveMaxConcurrent}, ` +
            `Remaining queued: ${pendingItems.length - itemsToProcess.length}`,
        );

        // Mark all items as being processed FIRST (batch update)
        itemsToProcess.forEach((item) => {
          activeUploadIds.add(item.id);
        });

        // Batch update the queue status
        setUploadQueue((prev) =>
          prev.map((qItem) =>
            itemIds.includes(qItem.id)
              ? { ...qItem, status: "uploading" }
              : qItem,
          ),
        );

        // Start all uploads in parallel (don't await)
        itemsToProcess.forEach((item) => {
          // Start upload without awaiting - let them run in parallel
          processUploadItem(item).catch((error) => {
            console.error(`Failed to process upload ${item.id}:`, error);
            // Clean up on error
            activeUploadIds.delete(item.id);
            setQueueVersion((v) => v + 1);
          });
        });
      } finally {
        isProcessingRef.current = false;
      }
    };

    // Small delay to batch rapid state changes
    const timeoutId = setTimeout(processQueue, 200);
    return () => clearTimeout(timeoutId);
  }, [
    uploadQueue,
    queueVersion, // Re-run when queue version changes
    autoUpload,
    contextId,
    effectiveMaxConcurrent,
  ]);

  const processUploadItem = async (item: UploadQueueItem) => {
    const startTime = Date.now();
    console.log(
      `🚀 Starting upload for: ${item.file.name} (${item.id}) - Active: ${activeUploadIds.size}`,
    );

    // Find the file
    const fileIndex = files.findIndex((f) => (f as any).uniqueId === item.id);
    if (fileIndex === -1) {
      console.error(`❌ File not found for upload: ${item.id}`);
      // Reset status if file not found
      activeUploadIds.delete(item.id);
      setUploadQueue((prev) =>
        prev.map((qItem) =>
          qItem.id === item.id
            ? { ...qItem, status: "failed", error: "File not found" }
            : qItem,
        ),
      );
      setQueueVersion((v) => v + 1); // Trigger queue re-processing
      return;
    }

    const file = files[fileIndex];
    const controller = new AbortController();
    uploadControllers.set(item.id, controller);

    // Mark file as uploading
    setFiles((prev) =>
      prev.map((f, idx) =>
        idx === fileIndex ? { ...f, isUploading: true } : f,
      ),
    );

    try {
      const chunkSize = calculateOptimalChunkSize(file.size);

      // Preserve original folder path from browser folder uploads
      const originalFile: File = (file as any).file || file;
      const relPath = originalFile.webkitRelativePath || undefined;

      const uploadPromise = uploadFileInChunks({
        context,
        contextId: String(contextId),
        file: originalFile,
        relativePath: relPath || undefined,
        chunkSize,
        onProgress: (progress) => {
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === fileIndex ? { ...f, uploadProgress: progress } : f,
            ),
          );
          setUploadQueue((prev) =>
            prev.map((qItem) =>
              qItem.id === item.id ? { ...qItem, progress } : qItem,
            ),
          );
        },
        onChunkProgress: (chunkIndex, isUploaded, error) => {
          setFiles((prev) =>
            prev.map((f, idx) => {
              if (idx !== fileIndex) return f;
              const chunkProgress = [...(f.chunkProgress || [])];
              chunkProgress[chunkIndex] = {
                chunkIndex,
                isUploaded,
                error,
                chunkHash: `${item.id}-chunk-${chunkIndex}`,
              };
              return {
                ...f,
                chunkProgress,
                uploadedChunks: chunkProgress.filter((c) => c.isUploaded)
                  .length,
              };
            }),
          );
        },
        onTotalChunks: (totalChunks) => {
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === fileIndex ? { ...f, totalChunks } : f,
            ),
          );
        },
        onUploadIdReceived: (uploadId) => {
          setFiles((prev) =>
            prev.map((f, idx) => (idx === fileIndex ? { ...f, uploadId } : f)),
          );
          setUploadQueue((prev) =>
            prev.map((qItem) =>
              qItem.id === item.id ? { ...qItem, uploadId } : qItem,
            ),
          );
        },
        onComplete: (result) => {
          // Update files and capture the updated file
          let updatedFile: UnifiedFileWithPreview | null = null;

          setFiles((prev) =>
            prev.map((f, idx) => {
              if (idx === fileIndex) {
                updatedFile = { ...f, isUploading: false, id: result?.id };
                return updatedFile;
              }
              return f;
            }),
          );

          setUploadQueue((prev) =>
            prev.map((qItem) =>
              qItem.id === item.id ? { ...qItem, status: "completed" } : qItem,
            ),
          );

          if (onUploadComplete && updatedFile) {
            // Pass the updated file with all its properties preserved
            onUploadComplete(updatedFile, result);
          }
        },
        onError: (error) => {
          // Enhanced error message for rate limits
          let userFriendlyError = error;
          if (
            error.includes("Upload limit reached") ||
            error.includes("Server is busy")
          ) {
            userFriendlyError =
              "Too many uploads in progress. This upload will retry automatically when slots become available.";
          }

          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === fileIndex
                ? { ...f, isUploading: false, error: userFriendlyError }
                : f,
            ),
          );
          setUploadQueue((prev) =>
            prev.map((qItem) =>
              qItem.id === item.id
                ? { ...qItem, status: "failed", error: userFriendlyError }
                : qItem,
            ),
          );

          // If it's a rate limit error, automatically retry after a delay
          if (
            error.includes("Upload limit reached") ||
            error.includes("Server is busy")
          ) {
            console.log(
              `⏰ Scheduling automatic retry for ${file.name} after delay...`,
            );
            setTimeout(() => {
              // Only retry if the item is still in failed status
              setUploadQueue((currentQueue) => {
                const currentItem = currentQueue.find((q) => q.id === item.id);
                if (currentItem && currentItem.status === "failed") {
                  console.log(
                    `🔄 Automatically retrying upload for ${file.name}`,
                  );
                  retryUpload(item.id);
                }
                return currentQueue;
              });
            }, 10000); // Wait 10 seconds before automatic retry
          }
        },
      });

      uploadPromises.set(item.id, uploadPromise);
      await uploadPromise;
    } catch (error) {
      console.error(`❌ Upload failed for ${file.name}:`, error);
    } finally {
      const duration = Date.now() - startTime;
      console.log(
        `🏁 Upload finished for: ${file.name} (${
          item.id
        }) - Duration: ${duration}ms, Active remaining: ${
          activeUploadIds.size - 1
        }`,
      );
      uploadControllers.delete(item.id);
      uploadPromises.delete(item.id);
      activeUploadIds.delete(item.id);
      // Trigger queue re-processing
      setQueueVersion((v) => v + 1);
    }
  };

  const handleFileUpload = async (acceptedFiles: File[]) => {
    // Check file count limit
    if (files.length + acceptedFiles.length > maxFiles) {
      const availableSlots = maxFiles - files.length;
      setUploadError(
        `Cannot upload ${acceptedFiles.length} files. Maximum ${maxFiles} files allowed. You have ${availableSlots} slots remaining.`,
      );
      return;
    }

    // Warn if adding files will exceed concurrent upload limit
    const currentPendingAndUploading = uploadQueue.filter(
      (item) => item.status === "pending" || item.status === "uploading",
    ).length;

    if (
      currentPendingAndUploading + acceptedFiles.length >
      effectiveMaxConcurrent
    ) {
      console.warn(
        `⚠️ Adding ${acceptedFiles.length} files will create a queue. ` +
          `Current uploads: ${currentPendingAndUploading}, ` +
          `Max concurrent: ${effectiveMaxConcurrent}`,
      );
    }

    // Clear previous error
    setUploadError(null);

    // Validate and prepare files
    const newFiles: UnifiedFileWithPreview[] = [];
    const newQueueItems: UploadQueueItem[] = [];
    const errors: string[] = [];

    for (const file of acceptedFiles) {
      // Validate file type for context
      const validation = validateFileForContext(file, context);
      if (!validation.isValid) {
        errors.push(validation.error || `Invalid file: ${file.name}`);
        continue;
      }

      // Custom validation if provided
      if (customValidation) {
        const customResult = customValidation(file);
        if (!customResult.isValid) {
          errors.push(customResult.error || `Invalid file: ${file.name}`);
          continue;
        }
      }

      // Check file size
      if (contextConfig?.maxFileSize && file.size > contextConfig.maxFileSize) {
        errors.push(
          `${file.name} exceeds maximum size of ${formatFileSize(
            contextConfig.maxFileSize,
          )}`,
        );
        continue;
      }

      // Create file with preview - extend file without modifying original
      const uniqueId = generateFileId();
      const fileWithPreview = {
        // Spread the file object to preserve all original properties
        ...file,
        // Explicitly preserve key File properties (read-only on File object)
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        // Convert lastModified timestamp to Date object for compatibility
        lastModifiedDate: new Date(file.lastModified),
        // Add our custom properties
        preview: URL.createObjectURL(file),
        sizeFormatted: formatFileSize(file.size),
        isUploading: false,
        uploadProgress: 0,
        totalChunks: 0,
        uploadedChunks: 0,
        chunkProgress: [],
        context,
        uniqueId,
        // Keep reference to original File object for upload
        file: file,
      } as UnifiedFileWithPreview;

      newFiles.push(fileWithPreview);

      // Add to upload queue if auto-upload is enabled
      if (autoUpload && contextId) {
        newQueueItems.push({
          id: uniqueId,
          file,
          context,
          contextId: String(contextId),
          status: "pending",
          progress: 0,
        });
      }
    }

    // Show errors if any
    if (errors.length > 0) {
      setUploadError(errors.join("\n"));
    }

    // Update state
    console.log(
      `✅ Adding ${newFiles.length} files to queue, ${newQueueItems.length} will be uploaded`,
    );
    setFiles((prev) => [...prev, ...newFiles]);
    setUploadQueue((prev) => [...prev, ...newQueueItems]);

    // Force immediate queue processing
    if (newQueueItems.length > 0) {
      setQueueVersion((v) => v + 1);
    }
  };

  const removeFile = async (index: number) => {
    const file = files[index];
    if (!file) return;

    const fileId = (file as any).uniqueId;

    // Cancel upload if in progress
    if (file.uploadId) {
      try {
        await cancelUploadAPI(file.uploadId);
      } catch (error) {
        console.error("Error cancelling upload:", error);
      }
    }

    // Abort any active upload
    const controller = uploadControllers.get(fileId);
    if (controller) {
      controller.abort();
    }

    // Revoke object URL
    if (file.preview) {
      URL.revokeObjectURL(file.preview);
    }

    // Remove from files and queue
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadQueue((prev) => prev.filter((item) => item.id !== fileId));
  };

  const cancelUpload = async (fileId: string) => {
    const file = files.find((f) => (f as any).uniqueId === fileId);
    if (!file) return;

    // Cancel on server if upload ID exists
    if (file.uploadId) {
      try {
        await cancelUploadAPI(file.uploadId);
      } catch (error) {
        console.error("Error cancelling upload:", error);
      }
    }

    // Abort local upload
    const controller = uploadControllers.get(fileId);
    if (controller) {
      controller.abort();
    }

    // Update status
    setFiles((prev) =>
      prev.map((f) =>
        (f as any).uniqueId === fileId
          ? { ...f, isUploading: false, error: "Cancelled" }
          : f,
      ),
    );
    setUploadQueue((prev) =>
      prev.map((item) =>
        item.id === fileId ? { ...item, status: "cancelled" } : item,
      ),
    );
  };

  const retryUpload = async (fileId: string) => {
    const queueItem = uploadQueue.find((item) => item.id === fileId);
    if (!queueItem || !contextId) return;

    // Reset status
    setUploadQueue((prev) =>
      prev.map((item) =>
        item.id === fileId
          ? { ...item, status: "pending", error: undefined, progress: 0 }
          : item,
      ),
    );

    const fileIndex = files.findIndex((f) => (f as any).uniqueId === fileId);
    if (fileIndex !== -1) {
      setFiles((prev) =>
        prev.map((f, idx) =>
          idx === fileIndex
            ? { ...f, isUploading: true, error: undefined, uploadProgress: 0 }
            : f,
        ),
      );
    }
  };

  const clearError = () => {
    setUploadError(null);
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
  } = useDropzone({
    onDrop: handleFileUpload,
    noClick: true,
    noKeyboard: true,
    // Chrome's showOpenFilePicker drops extension-only accept entries.
    useFsAccessApi: false,
    accept: contextConfig?.allowedExtensions?.join(","),
    maxSize: contextConfig?.maxFileSize,
    onDropRejected: (rejectedFiles) => {
      const errors = rejectedFiles.map((rejection) => {
        if (rejection.errors[0].code === "file-too-large") {
          return `${
            rejection.file.name
          } is too large. Max size is ${formatFileSize(
            contextConfig?.maxFileSize || 0,
          )}.`;
        }
        if (rejection.errors[0].code === "file-invalid-type") {
          return `${rejection.file.name} has an unsupported file type.`;
        }
        return `${rejection.file.name} could not be uploaded: ${rejection.errors[0].message}`;
      });
      setUploadError(errors.join("\n"));
    },
  });

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Cancel all active uploads
      uploadControllers.forEach((controller) => controller.abort());

      // Revoke all object URLs
      files.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  // Calculate isUploading from the queue state
  const isUploading = uploadQueue.some((item) => item.status === "uploading");

  return {
    files,
    uploadError,
    isDragActive,
    isUploading,
    uploadQueue,
    handleFileUpload,
    removeFile,
    cancelUpload,
    retryUpload,
    clearError,
    getRootProps,
    getInputProps,
    openFileDialog,
  };
};
